import React, { useState, useEffect, useRef } from 'react';
import Autocomplete from 'react-google-autocomplete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faTimes } from '@fortawesome/free-solid-svg-icons';
import './SearchBar.css';
import Config from '../utils/Config';
import { loadGoogleMapsAPI } from '../utils/functions';
import useTitle from '../hooks/useTitle';

const MyFFLSearchBar = ({ onSearch, setCurrentPage, onClear }) => {
  const [selectedAddress, setSelectedAddress] = useState('');
  const [coords, setCoords] = useState(null);
  const [radius, setRadius] = useState('5');          // default miles
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [autoKey, setAutoKey] = useState(0);          // 🔑 remount key
  const [sessionToken, setSessionToken] = useState(null);

  const autocompleteRef = useRef(null);

  useTitle('Search by Distance');

  useEffect(() => {
    onClear?.();      // nuke parent results when this search mounts
    setCurrentPage?.(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Google API
  useEffect(() => {
    loadGoogleMapsAPI(Config.API.GOOGLE_API_KEY)
      .then(() => setIsGoogleLoaded(true))
      .catch((err) => console.error('❌ Google Maps API failed:', err));
  }, []);

  // Refresh Places session token when loaded or when we remount input
  useEffect(() => {
    if (isGoogleLoaded && window.google?.maps?.places) {
      setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
    }
  }, [isGoogleLoaded, autoKey]);

  // Focus when ready
  useEffect(() => {
    if (isGoogleLoaded) {
      // small defer to ensure mounted
      setTimeout(() => autocompleteRef.current?.focus?.(), 0);
    }
  }, [isGoogleLoaded, autoKey]);

  const handlePlaceSelect = (place) => {
    if (!place || !place.geometry) {
      console.error('Invalid place selected.');
      return;
    }
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    setSelectedAddress(place.formatted_address.trim());
    setCoords({ lat, lng });
    setCurrentPage(1);

    handleSearch({ lat, lng, address: place.formatted_address });
  };

  const handleSearch = (overrideParams = {}) => {
    const searchCoords =
      (overrideParams.lat && overrideParams.lng) ? overrideParams : coords;

    if (!selectedAddress.trim() && !searchCoords) return; // nothing to search
    if (!searchCoords) return;

    setCurrentPage(1);
    onSearch({
      lat: searchCoords.lat,
      lng: searchCoords.lng,
      radius: overrideParams.radius || radius,
    });
  };

  const handleRadiusChange = (e) => {
    const newRadius = e.target.value;
    setRadius(newRadius);
    setCurrentPage(1);
    handleSearch({ lat: coords?.lat, lng: coords?.lng, radius: newRadius });
  };

  const clearSearch = () => {
    setSelectedAddress('');
    setCoords(null);
    setCurrentPage(1);

    // 🔑 Remount the Autocomplete to truly clear the internal value
    setAutoKey(k => k + 1);

    // Clear results in parent
    if (onClear) onClear();
    else onSearch({}); // empty params = clear signal

    // Re-focus after remount
    setTimeout(() => autocompleteRef.current?.focus?.(), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      clearSearch(); // ESC clears input + results
    }
  };

  return (
    <form className="search-bar" onKeyDown={handleKeyDown}>
      <div className="search-combobox">
        <div className="input-wrapper">
          {isGoogleLoaded ? (
            <Autocomplete
              id="geo-autocomplete"
              key={autoKey} // 🔑 remount on clear
              onPlaceSelected={handlePlaceSelect}
              options={{
                types: ['geocode'],
                componentRestrictions: { country: 'us' },
                sessionToken,
              }}
              placeholder="Enter your address"
              className="custom-autocomplete-input"
              ref={autocompleteRef}
            />
          ) : (
            <p>Loading Google Maps...</p>
          )}

          {(selectedAddress || coords) && (
            <button
              id="btn-clear"
              type="button"
              className="clear-btn"
              onClick={clearSearch}
              aria-label="Clear location search"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>

      <select
        id="select-radius"
        value={radius}
        onChange={handleRadiusChange}
        className="radius-dropdown"
      >
        <option value="">Select Radius</option>
        <option value="5">5 miles</option>
        <option value="10">10 miles</option>
        <option value="25">25 miles</option>
      </select>

      <button
        type="button"
        className="search-button"
        onClick={() => handleSearch()}
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </button>
    </form>
  );
};

export default MyFFLSearchBar;